/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@medflex/components/stories/Button';
import Spinner from '@medflex/components/stories/Spinner';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import InputCheckbox from '@medflex/components/stories/InputCheckbox';
import RequestTagsGroup from './RequestTagsGroup';

const RequestTagsEdit = ({
  userTagsAndUserTagGroups,
  widgetSettingsRequestTagIds,
  requestTagSelectionRequired,
  onSubmit,
}) => {
  const { t } = useTranslation('physician', {
    keyPrefix: 'InquiryManagementModule.RequestTags.form',
  });

  const formMethods = useForm({
    defaultValues: {
      requestTagSelectionRequired,
    },
  });

  const { register, handleSubmit: wrapSubmit, formState } = formMethods;

  const handleSubmit = useCallback(
    formData => {
      const { requestTagIds, ...rest } = formData;

      onSubmit({
        ...rest,
        requestTagIds: requestTagIds === false ? [] : [requestTagIds].flat(),
      });
    },
    [onSubmit],
  );

  const [optionGroups, options] = useMemo(
    () =>
      userTagsAndUserTagGroups.reduce(
        (prev, option) => {
          const [optionGroups, options] = prev;
          if (Array.isArray(option)) {
            optionGroups.push(option);
            return prev;
          }

          options.push(option);

          return prev;
        },
        [[], []],
      ),
    [userTagsAndUserTagGroups],
  );

  const renderOption = ({ value, label }) => (
    <label
      key={value}
      htmlFor={`requestTag-${value}`}
      className="relative flex cursor-pointer items-center p-3 hover:bg-slate-50 rounded"
    >
      <InputCheckbox
        id={`requestTag-${value}`}
        value={value}
        defaultChecked={widgetSettingsRequestTagIds.includes(value)}
        name="requestTagIds"
        ref={register}
      />
      <span className="whitespace-nowrap truncate ml-3 text-slate-900 text-sm font-semibold">
        {label}
      </span>
    </label>
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-col space-y-6">
        <div className=" bg-white rounded border border-slate-400 flex-col justify-start items-start divide-y divide-slate-400">
          {optionGroups.map((group, index) => {
            const { groupName } = group[0];
            return (
              <RequestTagsGroup key={index} title={groupName}>
                {group.map(option => renderOption(option))}
              </RequestTagsGroup>
            );
          })}
          {options.length > 0 && <div>{options.map(option => renderOption(option))}</div>}
        </div>

        <label
          key="requestTagSelectionRequired"
          htmlFor="requestTagSelectionRequired"
          className="relative flex cursor-pointer items-center text-sm font-normal"
        >
          <InputCheckbox
            id="requestTagSelectionRequired"
            name="requestTagSelectionRequired"
            ref={register}
          />
          <span className="whitespace-nowrap truncate ml-3 text-slate-900">
            <Trans i18nKey="required" t={t} className="text-slate-600 text-sm">
              <span className="font-semibold">Pflichtangabe</span>
            </Trans>
          </span>
        </label>

        <div className="flex space-x-8 items-center justify-end">
          <Button type="submit" isDisabled={formState.isSubmitting}>
            {formState.isSubmitting ? <Spinner /> : t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

RequestTagsEdit.propTypes = {
  userTagsAndUserTagGroups: PropTypes.arrayOf(PropTypes.array).isRequired,
  widgetSettingsRequestTagIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  requestTagSelectionRequired: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

RequestTagsEdit.defaultProps = {};

export default RequestTagsEdit;
